import React, { useState } from 'react';
import './UserRegistrationOverlay.css';
import TermsOverlay from '../terms/TermsOverlay';

interface UserRegistrationOverlayProps {
    onClose: () => void;
    onSuccess: () => void;
}

export default function UserRegistrationOverlay({ onClose, onSuccess }: UserRegistrationOverlayProps) {
    const [step, setStep] = useState<1 | 2>(1);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        authCode: '',
        password: '',
        passwordConfirm: ''
    });

    const [showAuthInput, setShowAuthInput] = useState(false);
    const [authSent, setAuthSent] = useState(false);
    const [authVerified, setAuthVerified] = useState(false);
    const [termsModal, setTermsModal] = useState<'service' | 'location' | 'privacy' | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSendAuthEmail = () => {
        if (!formData.email) {
            alert('이메일 주소를 입력해주세요.');
            return;
        }
        setAuthSent(true);
        setShowAuthInput(true);
        setAuthVerified(false);
    };

    const handleVerifyAuthCode = () => {
        if (formData.authCode.length < 6) {
            alert('인증번호 6자리를 입력해주세요.');
            return;
        }
        setAuthVerified(true);
        alert('이메일 인증이 완료되었습니다.');
    };

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email) {
            alert('기본 정보를 모두 입력해주세요.');
            return;
        }
        
        if (!authVerified) {
            alert('이메일 인증을 완료해주세요.');
            return;
        }
        
        setStep(2);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.password || !formData.passwordConfirm) {
            alert('비밀번호를 입력해주세요.');
            return;
        }
        
        if (formData.password !== formData.passwordConfirm) {
            alert('비밀번호가 일치하지 않습니다.');
            return;
        }

        if (formData.password.length < 10) {
            alert('비밀번호는 10자 이상이어야 합니다.');
            return;
        }

        alert('사용자 등록이 요청되었습니다. 관리자 승인 후 로그인해 주세요.');
        onSuccess();
    };

    return (
        <div className="user-reg-overlay">
            <div className="user-reg-container">
                
                {/* 1. LEFT PANEL */}
                <div className="user-reg-left-panel">
                    <div className="left-panel-logo">
                        <img src="/image/EVIEW_wh.png" alt="EView Logo" />
                    </div>
                    
                    <div className="left-panel-hero">
                        <h2>스마트한 EV Fleet<br/>관리의 시작</h2>
                        <p>EView 사용자 계정을 등록하고<br/>편리하게 차량 서비스를 이용하세요.</p>
                    </div>
                    
                    <div className="user-feature-cards">
                        <div className="user-feature-item">
                            <div className="feature-icon-wrap">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect><polyline points="17 2 12 7 7 2"></polyline></svg>
                            </div>
                            <div className="feature-text-wrap">
                                <h4>실시간 전기차 배터리 상태 확인</h4>
                                <p>예약한 차량의 배터리 잔량, 충전 상태, 위치를 한눈에 파악할 수 있어요.</p>
                            </div>
                        </div>
                        <div className="user-feature-item">
                            <div className="feature-icon-wrap">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                            </div>
                            <div className="feature-text-wrap">
                                <h4>자동화된 운행 일지 작성</h4>
                                <p>수기 작성 없이 탑승만 하면 자동으로 국세청 양식에 맞는 운행 일지가 생성됩니다.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="left-panel-trust">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                        <span>EView는 강력한 <strong>보안 정책</strong>으로 개인정보를 보호합니다.</span>
                    </div>
                </div>

                {/* 2. RIGHT PANEL */}
                <div className="user-reg-right-panel">
                    <button className="btn-close-modal" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                    
                    <div className="stepper-header">
                        <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>1. 기본 정보</div>
                        <div className="step-line"></div>
                        <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>2. 계정 설정</div>
                    </div>

                    <div className="form-scroll-area">
                        {step === 1 ? (
                            <form className="user-form" onSubmit={handleNext}>
                                <div className="form-intro">
                                    <h3>사용자 등록 신청</h3>
                                    <p>서비스 이용을 위한 기본 정보를 입력해주세요.</p>
                                </div>
                                
                                <div className="form-group">
                                    <h4 className="form-group-title">이름<span className="req">*</span></h4>
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                        <input type="text" name="name" placeholder="이름 (실명 입력)" value={formData.name} onChange={handleChange} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <h4 className="form-group-title">사내 이메일 인증<span className="req">*</span></h4>
                                    
                                    <div className="action-input-group">
                                        <div className="field">
                                            <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                                            <input 
                                                type="email" 
                                                name="email" 
                                                placeholder="이메일 주소" 
                                                value={formData.email} 
                                                onChange={handleChange} 
                                                disabled={authSent}
                                            />
                                        </div>
                                        <button 
                                            type="button" 
                                            className={`btn-inline-action ${authSent ? '' : 'primary'}`}
                                            onClick={handleSendAuthEmail}
                                            disabled={authSent}
                                        >
                                            {authSent ? '발송됨' : '인증번호 받기'}
                                        </button>
                                    </div>

                                    {showAuthInput && (
                                        <div className="action-input-group slide-down" style={{ marginTop: '12px' }}>
                                            <div className="field">
                                                <input 
                                                    type="text" 
                                                    name="authCode" 
                                                    placeholder="인증번호 6자리" 
                                                    value={formData.authCode} 
                                                    onChange={handleChange} 
                                                    maxLength={6}
                                                    disabled={authVerified}
                                                    style={{ paddingLeft: '16px' }}
                                                />
                                                {!authVerified && <span className="auth-timer">03:00</span>}
                                            </div>
                                            <button 
                                                type="button" 
                                                className={`btn-inline-action ${authVerified ? 'success' : 'primary'}`}
                                                onClick={handleVerifyAuthCode}
                                                disabled={authVerified}
                                            >
                                                {authVerified ? '인증 완료 ✓' : '확인'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="form-actions-row">
                                    <button type="submit" className="btn-submit" style={{ width: '100%' }}>다음 단계로 진행</button>
                                </div>
                            </form>
                        ) : (
                            <form className="user-form" onSubmit={handleSubmit}>
                                <div className="form-intro">
                                    <h3>보안 계정 설정</h3>
                                    <p>로그인에 사용할 안전한 비밀번호를 설정해주세요.</p>
                                </div>

                                <div className="form-group">
                                    <h4 className="form-group-title">비밀번호<span className="req">*</span></h4>
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                        <input type="password" name="password" placeholder="비밀번호 설정 (영문, 숫자 10자 이상)" value={formData.password} onChange={handleChange} required />
                                    </div>
                                    <div className="field">
                                        <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                        <input type="password" name="passwordConfirm" placeholder="비밀번호 한 번 더 입력" value={formData.passwordConfirm} onChange={handleChange} required />
                                    </div>
                                    <div className="pwd-checks">
                                        <span className={`check ${formData.password.length >= 10 ? 'ok' : ''}`}>10자 이상</span>
                                        <span className={`check ${/[a-zA-Z]/.test(formData.password) ? 'ok' : ''}`}>영문</span>
                                        <span className={`check ${/\d/.test(formData.password) ? 'ok' : ''}`}>숫자</span>
                                        <span className={`check ${(formData.password && formData.password === formData.passwordConfirm) ? 'ok' : ''}`}>일치 확인</span>
                                        <span className="strength">
                                            <span className={`bar ${formData.password.length >= 6 ? 'filled' : ''}`}></span>
                                            <span className={`bar ${formData.password.length >= 10 ? 'filled' : ''}`}></span>
                                            <span className={`bar ${formData.password.length >= 12 ? 'filled' : ''}`}></span>
                                        </span>
                                    </div>
                                </div>

                                <div className="form-actions-row">
                                    <button type="button" className="btn-back" onClick={() => setStep(1)}>이전</button>
                                    <button type="submit" className="btn-submit">등록 완료 대기</button>
                                </div>
                                <p className="terms">
                                    가입하면 <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('service'); }}>서비스 이용약관 ↗</a>과 <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('location'); }}>위치정보 이용약관 ↗</a>,<br />
                                    <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('privacy'); }}>개인정보 수집 및 이용 ↗</a>에 동의한 것으로 봅니다.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            <div className="login-side-content">
                <h1 className="side-title">운전부터 정비까지, 한 곳에서</h1>
                <h2 className="side-subtitle">스마트한 당신의 차량관리 시스템</h2>
            </div>

            {termsModal && (
                <TermsOverlay type={termsModal} onClose={() => setTermsModal(null)} />
            )}
        </div>
    );
}
